// client/src/services/lexistarDrivers.js
//
// LexiStar driver register API client (ST-00188, M3-A2; contracts
// docs/lexistar/contracts/m3-contracts.md §3.2, §7.2; LS-09 §5.9). Server:
// server/modules/lexistar/drivers, mounted at /api/lexistar.
//
// Thin wrapper over the shared axios instance in `services/api` (cookie-session auth,
// CSRF header, single-flight 401 refresh; `api` already carries `/api`). Every helper
// resolves to the response BODY. Payloads use the DB column names (snake_case). Action
// routes use the contract's `:verb` suffix literally (POST /drivers:import).
//
// Response shapes:
//   listDrivers        { drivers: [driver + consents {channel: {state, method, at}} +
//                        current_allocation {id, asset_id, registration, fleet_no, from, to} | null],
//                        pagination }
//   getDriver          { driver, consents: { current, history[] }, allocations[], messages[] (last 20) }
//   createDriver / updateDriver   { driver }   (409 { field: 'tag'|'phone_e164', driver_id } on a
//                        duplicate in the organisation; 409 { current_version } on a stale version)
//   recordConsent      { consent, current[] }
//   listAllocations    { allocations: [{id, driver_id, driver_name, driver_tag, asset_id,
//                        registration, fleet_no, from, to, cancelled, source, created_by}], pagination }
//   createAllocation   { allocation }  (409 { conflicts[] } when the asset already has a driver
//                        in that period)
//   closeAllocation    { allocation, closed_at }  (a running one ends now, a future one is
//                        cancelled; 409 once it has ended — FR-DRV-003)
//   importDrivers      { tenant_id, dry_run, created, updated, consents, failed,
//                        rows: [{line, action: create|update|error, driver_id, name, tag, errors[]}] }
//                        (400 with the same rows when any row is invalid and partial is off)

import api from './api';

const LX = '/lexistar';

/** Drop undefined / null / '' so the API never sees `q=`. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
const body = (res) => res.data;

// ── Drivers ──────────────────────────────────────────────────────────────────

/** Filters: q (name, tag, phone), active, page, limit. */
export const listDrivers = (params) =>
  api.get(`${LX}/drivers`, { params: clean(params) }).then(body);

export const getDriver = (id) => api.get(`${LX}/drivers/${id}`).then(body);

/**
 * { name, tag?, language?: 'en'|'af', phone_e164? (any common form; normalised to E.164),
 *   telegram_chat_id?, preferred_channel?: 'whatsapp'|'telegram'|'sms'|'fake', active?, notes? }
 */
export const createDriver = (data) => api.post(`${LX}/drivers`, clean(data)).then(body);

/** Any driver field; pass `version` for optimistic concurrency. */
export const updateDriver = (id, data) => api.patch(`${LX}/drivers/${id}`, data).then(body);

/**
 * FR-NTF-011 consent evidence (append-only):
 * { channel, state: 'opted_in'|'opted_out', method: 'form'|'keyword'|'import'|'verbal'|'system',
 *   wording (required for opted_in), evidence?: {...}, at? (ISO) }
 */
export const recordConsent = (driverId, data) =>
  api.post(`${LX}/drivers/${driverId}/consents`, data).then(body);

/**
 * CSV bulk import. `file` is a File/Blob (sent as multipart `file`) or CSV text.
 * Options: { dry_run, partial, consent_wording (required when the file has an opt-in column) }.
 * Columns (any case/spacing): name, tag|employee_no, phone|mobile, language, telegram_chat_id,
 * preferred_channel, active, notes, opt_in (e.g. "whatsapp;sms").
 */
export function importDrivers(file, { dry_run, partial, consent_wording } = {}) {
  const params = clean({ dry_run, partial, consent_wording });
  if (typeof file === 'string') {
    return api.post(`${LX}/drivers:import`, { csv: file }, { params }).then(body);
  }
  const form = new FormData();
  form.append('file', file);
  return api.post(`${LX}/drivers:import`, form, { params }).then(body);
}

// ── Driver–vehicle allocations ───────────────────────────────────────────────

/** Filters: driver_id, asset_id, at (ISO), from, to, current (true), page, limit. */
export const listAllocations = (params) =>
  api.get(`${LX}/driver-allocations`, { params: clean(params) }).then(body);

/** { driver_id, asset_id, from (ISO), to? (ISO, open-ended when absent), source?: 'manual'|'tag'|'dispatch' } */
export const createAllocation = (data) =>
  api.post(`${LX}/driver-allocations`, clean(data)).then(body);

/** Close (running) or cancel (future) an allocation. */
export const closeAllocation = (id) => api.delete(`${LX}/driver-allocations/${id}`).then(body);
